import React, { useEffect, useMemo, useState } from 'react';
import Drawer, { DrawerProps } from '@app/component/overlays/drawer/Drawer';
import ReviewCategoryOutput from '@app/model/review/output/ReviewCategoryOutput';
import ReviewsStateStatistics from '@app/model/review/state/ReviewsStateStatistics';
import { useReviewsTools } from '@app/site/secured/review-manager/provider/useReviewsTools';
import useDrawer from '@app/component/overlays/drawer/DrawerRef';
import { ReviewService } from '@app/model/review/service/ReviewService';
import { useQuery } from '@tanstack/react-query';
import { ReviewsQueryKeys } from '@app/model/review/service/ReviewsQueryKeys';
import TablePlaceholder from '@app/component/placeholder/TablePlaceholder';
import Message from '@app/component/message/Message';
import _isEmpty from 'lodash/isEmpty';
import {
	ArrowTrendingUpIcon,
	ChatBubbleLeftIcon,
	HandThumbDownIcon,
	HandThumbUpIcon,
} from '@heroicons/react/24/outline';
import Utilities from '@util/Utilities';
import Review from '@app/model/review/Review';
import ReviewScoreTag from '@app/site/secured/review-manager/fragment/reviews-inbox/component/ReviewScoreTag';
import { findProviderData } from '@app/component/provider/FindProviderData';
import { ReviewText } from '@app/model/review/ReviewText';
import ReviewTextTopic from '@app/model/review/ReviewTextTopic';
import ReviewTextTopicSentiment from '@app/model/review/ReviewTextTopicSentiment';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import ReviewTextType from '@app/model/review/ReviewTextType';

type ReviewsCategoryAnalysisDrawerProps = Omit<DrawerProps, 'title' | 'ref'> & {
	hotelId: number
	statistics: ReviewsStateStatistics
	category: ReviewCategoryOutput
}

const ReviewsCategoryAnalysisDrawer = React.memo(
	function ReviewsCategoryAnalysisDrawer({
																					 statistics,
																					 hotelId,
																					 category,
																					 ...props
																				 }: ReviewsCategoryAnalysisDrawerProps) {

		const drawer = useDrawer();
		useReviewsTools();

		useEffect(() => {
			drawer.open();
		}, [category]);


		return <Drawer ref={drawer.ref} title={category.name} {...props}>
			<ReviewsCategoryAnalysis statistics={statistics} hotelId={hotelId} category={category} />
		</Drawer>;
	},
);


const ReviewsCategoryAnalysis: React.FunctionComponent<{
	statistics: ReviewsStateStatistics,
	hotelId: number,
	category: ReviewCategoryOutput
}> = ({ statistics, hotelId, category }) => {
	const tools = useReviewsTools();

	const { data, isLoading, isSuccess } = useQuery({
		queryFn: () => ReviewService.findReviewsForTextCategory(tools.filter.companyHotelId, hotelId, category.id),
		queryKey: ReviewsQueryKeys.categoryReviews(tools.filter.companyHotelId, hotelId, category.id),
		refetchOnWindowFocus: false,
	});

	if (isLoading)
		return <div className="mt-2 px-6 py-5 space-y-6"><TablePlaceholder /></div>;

	if (isSuccess) {

		if (_isEmpty(data))
			return <div className="mt-2 px-6 py-5 space-y-6"><Message state="info">No reviews found for this
				category!</Message></div>;

		return <div className="mt-2 px-6 py-5 space-y-6">
			<div className="text-sm text-pretty">
				<p className="text-gray-600 mb-4">
					Find below a detailed analysis of guest feedback for this category.
				</p>
			</div>
			<div>
				<div className="sm:flex items-center justify-between gap-x-2 mt-2">
					<div className="flex items-center gap-x-1">
						<ChatBubbleLeftIcon className="h-4 w-4 text-gray-500" />
						<span className="text-xs text-gray-500">Total Reviews:</span><span
						className="text-xs font-bold text-gray-800">{data.length}</span></div>
					<div className="flex items-center gap-x-1">
						<ArrowTrendingUpIcon className="h-4 w-4 text-gray-500" />
						<span className="text-xs text-gray-500">Score:</span><span
						className="text-xs font-bold text-gray-800">{Utilities.formatDouble(statistics.score, 1)}</span>
					</div>
					<div className="flex items-center gap-x-3">
						<div className="flex items-center gap-x-1">
							<HandThumbUpIcon className="w-4 h-4 text-green-500" />
							<span className="text-xs font-normal text-gray-800">{statistics.positiveSentimentCount}</span></div>
						<div className="flex items-center gap-x-1">
							<HandThumbDownIcon className="w-4 h-4 text-red-500" />
							<span className="text-xs font-normal text-gray-800">{statistics.negativeSentimentCount}</span></div>
					</div>
				</div>
				<hr className="mt-2 mb-6" />
				<RenderGraph reviews={data} />
				<RenderReviewsList reviews={data} />
			</div>
		</div>;
	}

	return <div className="mt-2 px-6 py-5 space-y-6"><Message state="error">Reviews could not be loaded at the
		moment!</Message></div>;
};

const RenderGraph: React.FunctionComponent<{ reviews: Review[] }> = ({ reviews }) => {
	const options: Highcharts.Options = useMemo(() => {
		type MonthlyBucket = {
			key: string;
			label: string;
			sortValue: number;
			positive: number;
			negative: number;
		};

		const buckets = new Map<string, MonthlyBucket>();
		const monthFormatter = new Intl.DateTimeFormat('en', { month: 'short' });
		const now = new Date();
		const startMonth = new Date(now.getFullYear(), now.getMonth() - 4, 1);

		const monthDates: Date[] = [];
		for (let i = 0; i < 5; i += 1) {
			monthDates.push(new Date(startMonth.getFullYear(), startMonth.getMonth() + i, 1));
		}

		monthDates.forEach((d) => {
			const year = d.getFullYear();
			const month = d.getMonth();
			const key = `${year}-${String(month + 1).padStart(2, '0')}`;
			const sortValue = year * 100 + (month + 1);
			const label = `${monthFormatter.format(new Date(year, month, 1))} ${year}`;
			buckets.set(key, {
				key,
				label,
				sortValue,
				positive: 0,
				negative: 0,
			});
		});

		reviews.forEach((review) => {
			const reviewDate = new Date(review.reviewDate || review.creationDate);
			if (Number.isNaN(reviewDate.getTime())) return;
			if (reviewDate < startMonth || reviewDate > now) return;

			const year = reviewDate.getFullYear();
			const month = reviewDate.getMonth();
			const key = `${year}-${String(month + 1).padStart(2, '0')}`;

			const bucket = buckets.get(key);
			if (!bucket) return;

			const topics = (review.getTopics ? review.getTopics() : []) as ReviewTextTopic[];
			topics.forEach((t) => {
				switch (t.sentiment) {
					case ReviewTextTopicSentiment.POSITIVE:
					case ReviewTextTopicSentiment.VERY_POSITIVE:
						bucket.positive += 1;
						break;
					case ReviewTextTopicSentiment.NEGATIVE:
					case ReviewTextTopicSentiment.VERY_NEGATIVE:
						bucket.negative += 1;
						break;
					default:
						break;
				}
			});

		});

		const sortedBuckets = [...buckets.values()].sort((a, b) => a.sortValue - b.sortValue);
		const categories = sortedBuckets.map((b) => b.label);
		const positiveData = sortedBuckets.map((b) => b.positive);
		const negativeData = sortedBuckets.map((b) => b.negative);

		return {
			chart: {
				type: 'column',
				height: 260,
			},
			title: {
				text: undefined,
			},
			xAxis: {
				categories,
				tickLength: 0,
				lineColor: '#e5e7eb',
			},
			yAxis: {
				title: {
					text: undefined,
				},
				min: 0,
				allowDecimals: false,
				gridLineColor: '#f3f4f6',
				labels: {
					formatter: function() {
						return String(this.value);
					},
				},
			},
			legend: {
				align: 'right',
				verticalAlign: 'top',
			},
			tooltip: {
				shared: true,
				formatter: function() {
					const points = (this.points ?? []) as any[];
					const header =
						(points[0]?.point as any)?.category ??
						(points[0]?.point as any)?.name ??
						this.x;
					const lines = points.map((p) => {
						const y = p.y;
						return `<span style="color:${p.color}">●</span> ${p.series.name}: <b>${y}</b>`;
					});
					return `<b>${header}</b><br/>${lines.join('<br/>')}`;
				},
			},
			plotOptions: {
				column: {
					stacking: 'normal',
					borderWidth: 0,
					pointPadding: 0.1,
					groupPadding: 0.2,
				},
			},
			series: [
				{
					name: 'Negative',
					type: 'column',
					data: negativeData,
					color: '#ef4444',
				},
				{
					name: 'Positive',
					type: 'column',
					data: positiveData,
					color: '#10b981',
				},
			],
			credits: {
				enabled: false,
			},
		};
	}, [reviews]);

	return <div className="overflow-hidden rounded-lg bg-white shadow px-4 py-4">
		<div className="mb-2">
			<h2 className="text-sm font-semibold text-gray-900">Mentions over time</h2>
		</div>
		<HighchartsReact
			highcharts={Highcharts}
			options={options}
			containerProps={{ style: { height: '260px', width: '100%' } }}
		/>
	</div>;
};

const RenderReviewsList: React.FunctionComponent<{ reviews: Review[] }> = ({ reviews }) => {

	const [orderedReviews, setOrderedReviews] = useState(reviews);

	useEffect(() => {
		setOrderedReviews([...reviews]);
	}, [reviews]);

	const sortReviews = (sortType: string) => {
		if (sortType === 'MR') {
			setOrderedReviews([...reviews]);
		} else if (sortType === 'LR') {
			setOrderedReviews([...reviews].sort((a, b) => a.score - b.score));
		} else if (sortType === 'HR') {
			setOrderedReviews([...reviews].sort((a, b) => b.score - a.score));
		}
	};

	return <div className="mt-3">
		<div className="sm:flex items-baseline justify-between mb-2">
			<h3 className="shrink-0 text-sm font-medium text-gray-900">• Reviews mentioning this topic</h3>
			<select className="input py-0 h-8 max-w-[200px]" onChange={e => sortReviews(e.target.value)}>
				<option value="MR">Most Recent</option>
				<option value="LR">Lowest Rating</option>
				<option value="HR">Higher Rating</option>
			</select>
		</div>
		<ul role="list" className="space-y-4">
			{orderedReviews.map(review => <li key={review.id}
																				className="flex-auto rounded-md p-4 bg-gray-50 ring-1 ring-inset ring-gray-200">
				<div className="sm:flex sm:items-baseline sm:justify-between mb-3">
					<div className="flex flex-wrap items-center gap-x-2">
						<h3 className="text-sm font-medium text-gray-900">{review.customerInfo.username ?? 'Unknown'}</h3>
						<ReviewScoreTag score={review.score} />
						<div className="inline-block shrink-0 relative">
							{findProviderData(review.provider).icon}
						</div>
						<p className="text-xs font-light italic">{`(${review.id})`}</p>
					</div>
					<p
						className="mt-1 whitespace-nowrap text-xs text-gray-600 sm:ml-3 sm:mt-0">{Utilities.formatDateString(review.reviewDate, 'DD MMM YYYY')}</p>
				</div>
				{review.texts.filter(t => t.type === ReviewTextType.POSITIVE || t.type === ReviewTextType.NEGATIVE)
					.map(text => <RenderText key={text.id} text={text} />)}
			</li>)}
		</ul>
	</div>;
};

const RenderText: React.FunctionComponent<{ text: ReviewText }> = ({ text }) => {
	const content = text.translation || text.text || '';

	const highlighted = useMemo(() => {
		const topics = (text.getTopics ? text.getTopics() : text.topics || []) as ReviewTextTopic[];
		if (_isEmpty(topics) || !content) return [content];

		type Highlight = {
			from: number;
			to: number;
			sentiment: ReviewTextTopicSentiment;
			label: string;
		};

		const highlights: Highlight[] = topics
			.map((t) => {
				let from: number | undefined = t?.topicFragmentPosition?.from;
				let to: number | undefined = t?.topicFragmentPosition?.to;
				if (typeof from !== 'number' || typeof to !== 'number') {
					from = undefined;
					to = undefined;
				}

				if (typeof from === 'number' && typeof to === 'number') {
					from = Math.max(0, Math.min(from, content.length));
					to = Math.max(0, Math.min(to, content.length));
					if (to <= from) {
						from = undefined;
						to = undefined;
					}
				}

				if ((from === undefined || to === undefined) && t?.topicFragment) {
					const idx = content.toLowerCase().indexOf(t.topicFragment.toLowerCase());
					if (idx >= 0) {
						from = idx;
						to = idx + t.topicFragment.length;
					}
				}

				if (from === undefined || to === undefined) return null;
				return {
					from,
					to,
					sentiment: t.sentiment,
					label: t.category?.name || t.topicFragment,
				};
			})
			.filter((h): h is Highlight => !!h)
			.sort((a, b) => a.from - b.from || a.to - b.to);

		if (_isEmpty(highlights)) return [content];

		const nodes: React.ReactNode[] = [];
		let cursor = 0;
		highlights.forEach((h, index) => {
			const start = Math.max(h.from, cursor);
			const end = Math.max(start, h.to);
			if (start > cursor) nodes.push(content.slice(cursor, start));
			if (end > start) {
				const className = getSentimentHighlightClassName(h.sentiment);
				nodes.push(
					<span key={`${start}-${end}-${index}`} className={className} title={h.label}>
						{content.slice(start, end)}
					</span>,
				);
				cursor = end;
			}
		});
		if (cursor < content.length) nodes.push(content.slice(cursor));
		return nodes;
	}, [content, text]);

	return <p className="text-sm/5 text-gray-500 whitespace-pre-wrap">{highlighted}</p>;
};

const getSentimentHighlightClassName = (sentiment: ReviewTextTopicSentiment) => {
	switch (sentiment) {
		case ReviewTextTopicSentiment.VERY_POSITIVE:
			return 'rounded px-1 bg-green-200 text-green-900';
		case ReviewTextTopicSentiment.POSITIVE:
			return 'rounded px-1 bg-green-100 text-green-900';
		case ReviewTextTopicSentiment.NEUTRAL:
			return 'rounded px-1 bg-gray-200 text-gray-900';
		case ReviewTextTopicSentiment.NEGATIVE:
			return 'rounded px-1 bg-red-100 text-red-900';
		case ReviewTextTopicSentiment.VERY_NEGATIVE:
			return 'rounded px-1 bg-red-200 text-red-900';
		default:
			return 'rounded px-1 bg-yellow-100 text-yellow-900';
	}
};

export default ReviewsCategoryAnalysisDrawer;
